import { Box, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

const useStyles = makeStyles(({ spacing, typography, radius }) => ({
  root: {
    height: '100%'
  },
  contentContainer: {
    width: '100%',
    paddingTop: spacing(2),
    paddingBottom: spacing(2)
  },
  // `&&` selector trick bumps CSS specificity so this override beats the
  // base `cardTitle` class from card-content.js without `!important`.
  // See generic-card.js for the same pattern.
  cardTitle: {
    '&&': {
      ...typography.heading.h5,
      fontWeight: typography.weight.extra
    }
  },
  cardSummary: {
    '&&': {
      ...typography.body.s,
      fontWeight: typography.weight.regular,
      lineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  imageContainer: {
    width: '100%',
    // Flex to fill the remaining vertical space inside the card-content
    // children slot (see card-content.js comment). minHeight keeps the
    // 138px floor so short cards still have a usable image area.
    flex: 1,
    minHeight: spacing(17),
    marginTop: spacing(2),
    marginBottom: spacing(1)
  },
  image: {
    height: '100%',
    width: '100%',
    borderRadius: radius.lg,
    objectFit: 'cover'
  }
}))

const PromotedContentSecondaryCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, ...props }) => {
  const classes = useStyles()

  const {
    contentImage,
    handleCardSelect,
    expanded,
    cardSize,
    actions
  } = usePromotedCardMethods({ content, position, size, cardType: PROMOTED_CARD_TYPES.SECONDARY, onSelect, onAddToPlaylist })

  const handleImageClick = (e) => {
    e.stopPropagation() // Prevent content area from catching this
    handleCardSelect()
  }

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation() // Prevent content area from catching this
      handleCardSelect()
    }
  }

  return (
    <PromotedCardContainer
      expand={expanded}
      size={cardSize}
      actions={actions}
      content={content}
      testId={testId}
    >
      <PromotedContentCardContent
        content={content}
        actions={actions}
        expand={expanded}
        cardType={PROMOTED_CARD_TYPES.SECONDARY}
        onSelect={handleCardSelect}
        overrides={{
          contentContainer: classes.contentContainer,
          cardTitle: classes.cardTitle,
          cardSummary: classes.cardSummary
        }}
      >
        <Box
          className={classes.imageContainer}
          onClick={handleImageClick}
          onKeyDown={handleImageKeyDown}
          role='button'
          tabIndex={0}
          aria-label={content ? `View ${content.title} image` : 'Loading content image'}
          sx={{
            cursor: 'pointer',
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px',
              borderRadius: '8px'
            }
          }}
        >

          {
            content
              ? (
                <img
                  src={contentImage}
                  title={content?.image?.altText}
                  className={classes.image}
                  data-test={`card-image-${content?.friendlyUrl}`}
                  alt={content?.image?.altText || content?.title}
                />
                )
              : (
                <Skeleton variant='rectangular' className={classes.image} />
                )
          }
        </Box>
      </PromotedContentCardContent>
    </PromotedCardContainer>
  )
}

export default PromotedContentSecondaryCard
